<template>
  <div class="omx-side-root">
    <!-- Scrim lives OUTSIDE the aside: the aside is transformed on mobile, which would
         make it the containing block for this position:fixed overlay and collapse it. -->
    <button class="omx-side__scrim" v-if="openMobile" @click="$emit('close-mobile')" aria-label="Close menu"></button>

    <aside class="omx-side" :class="{ 'is-collapsed': effectiveCollapsed, 'is-open-mobile': openMobile }" :aria-expanded="!effectiveCollapsed">
    <div class="omx-side__inner">
      <div class="omx-side__brand">
        <router-link :to="brandTo" class="omx-side__brand-link" :aria-label="$brand.name">
          <img class="omx-side__logo-img" :src="$brand.logo" :alt="$brand.name" />
          <span class="omx-side__brand-text" v-show="!effectiveCollapsed">
            <b>{{ $brand.name }}</b>
            <small>{{ $t('shell.workspace') }}</small>
          </span>
        </router-link>
        <button v-if="!isMobile" class="omx-side__collapse" @click="$emit('toggle')" :title="collapsed ? $t('shell.expand') : $t('shell.collapse')">
          <b-icon :icon="collapsed ? 'chevron-right' : 'chevron-left'" size="is-small" />
        </button>
      </div>

      <nav class="omx-side__nav" v-if="loggedIn">
        <div class="omx-side__group" v-for="(group, gi) in groups" :key="gi">
          <span class="omx-side__group-label" v-show="!effectiveCollapsed">{{ $t(group.labelKey) }}</span>
          <router-link
              v-for="item in group.items"
              :key="item.to"
              :to="item.to"
              class="omx-side__item"
              active-class="is-active"
              :title="effectiveCollapsed ? $t(item.labelKey) : null"
          >
            <b-icon :icon="item.icon" size="is-small" class="omx-side__ico" />
            <span class="omx-side__item-label" v-show="!effectiveCollapsed">{{ $t(item.labelKey) }}</span>
            <span class="omx-side__item-count" v-if="item.count != null && !collapsed">{{ item.count }}</span>
          </router-link>
        </div>

        <!-- Database (endpoint) — surfaced to all roles for demos, when >1 dataset -->
        <div class="omx-side__group" v-if="loggedIn && endpoints.length > 1">
          <span class="omx-side__group-label" v-show="!effectiveCollapsed">{{ $t('shell.database') }}</span>
          <div class="omx-side__dev" v-show="!effectiveCollapsed">
            <div class="omx-side__dev-row">
              <b-select v-model="endpointLocal" size="is-small" expanded @input="onEndpointChange">
                <option v-for="ep in endpoints" :value="ep" :key="ep">{{ endpointLabel(ep) }}</option>
              </b-select>
            </div>
          </div>
          <!-- collapsed: compact icon hint -->
          <div class="omx-side__item" v-show="effectiveCollapsed" :title="$t('shell.database')">
            <b-icon icon="database" size="is-small" class="omx-side__ico" />
          </div>
        </div>

        <!-- Language — available to every user -->
        <div class="omx-side__group" v-if="loggedIn">
          <span class="omx-side__group-label" v-show="!effectiveCollapsed">{{ $t('shell.language') }}</span>
          <div class="omx-side__dev" v-show="!effectiveCollapsed">
            <div class="omx-side__dev-row">
              <b-select v-model="langLocal" size="is-small" expanded @input="onLangChange">
                <option v-for="lang in languages" :value="lang" :key="lang">{{ lang.toUpperCase() }}</option>
              </b-select>
            </div>
          </div>
          <!-- collapsed: compact icon hint -->
          <div class="omx-side__item" v-show="effectiveCollapsed" :title="$t('shell.language')">
            <b-icon icon="translate" size="is-small" class="omx-side__ico" />
          </div>
        </div>
      </nav>

      <div class="omx-side__user" v-if="loggedIn">
        <router-link :to="{ name: 'UserDetails', params: { id: '_self' } }" event="" @click.native.prevent="$emit('account')" class="omx-side__user-card">
          <span class="omx-side__avatar">{{ initials }}</span>
          <span class="omx-side__user-meta" v-show="!effectiveCollapsed">
            <b>{{ usernameShort }}</b>
            <small>{{ roleLabel }}</small>
          </span>
          <button class="omx-side__user-more" v-show="!effectiveCollapsed" @click.stop.prevent="$emit('logout')" :title="$t('top-menu.sign-out')">
            <b-icon icon="logout" size="is-small" />
          </button>
        </router-link>
      </div>

      <footer class="omx-side__footer" v-show="!effectiveCollapsed">
        <OmxBugReport class="omx-side__footer-bug" />
        <span class="omx-side__footer-copy">
          © {{ currentYear }}
          <a href="https://bald.studio" target="_blank" rel="noopener noreferrer">bald.studio</a>
        </span>
      </footer>
    </div>
    </aside>
  </div>
</template>

<script>
import OmxBugReport from "@/components/shell/OmxBugReport.vue";

export default {
  name: "OmxSidebar",
  components: { OmxBugReport },
  props: {
    loggedIn: { type: Boolean, default: false },
    username: { type: String, default: "" },
    role: { type: String, default: null },
    collapsed: { type: Boolean, default: false },
    openMobile: { type: Boolean, default: false }
  },
  data() {
    return {
      languages: ['fr', 'en'],
      endpoints: this.$brand.endpoints,
      endpointLocal: this.getSubmitEndpoint(),
      langLocal: this.$i18n.locale,
      isMobile: false
    };
  },
  computed: {
    currentYear() {
      return new Date().getFullYear();
    },
    // On mobile the sidebar is a full-width drawer; the desktop "collapsed"
    // (icon-only) state doesn't apply there, so ignore it below 900px.
    effectiveCollapsed() {
      return this.isMobile ? false : this.collapsed;
    },
    isSystem() {
      return this.currentUserSystemAccessLevel();
    },
    brandTo() {
      return this.loggedIn ? '/projects' : '/login';
    },
    initials() {
      if (!this.username) return "·";
      const base = this.username.split("@")[0];
      const parts = base.split(/[._-]/).filter(Boolean);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0][0] + parts[1][0]).toUpperCase();
    },
    usernameShort() {
      if (!this.username) return "";
      const base = this.username.split("@")[0];
      return base.length > 18 ? base.slice(0, 18) + "…" : base;
    },
    roleLabel() {
      if (!this.role) return "";
      const key = `shell.roles.${this.role}`;
      const translated = this.$t(key);
      return translated === key ? this.role : translated;
    },
    groups() {
      const items = [];

      const workspace = { labelKey: 'shell.workspace', items: [] };
      if (this.currentUserAccess('Projects', this.UserActions.VIEW)) {
        workspace.items.push({ to: '/projects', icon: 'view-grid-outline', labelKey: 'top-menu.projects' });
      }
      if (this.currentUserAccess('ProjectTemplates', this.UserActions.VIEW)) {
        workspace.items.push({ to: '/templates', icon: 'content-duplicate', labelKey: 'top-menu.project-templates' });
      }
      // Help & demo guide — available to every role (sales-enablement).
      workspace.items.push({ to: '/help', icon: 'help-circle-outline', labelKey: 'top-menu.help' });
      if (workspace.items.length) items.push(workspace);

      const admin = { labelKey: 'shell.admin', items: [] };
      if (this.currentUserAccess('Users', this.UserActions.VIEW)) {
        admin.items.push({ to: '/users', icon: 'account-group', labelKey: 'top-menu.users' });
      }
      if (this.currentUserAccess('CategoricalData', this.UserActions.VIEW)) {
        admin.items.push({ to: '/categories', icon: 'shape', labelKey: 'top-menu.categories' });
      }
      if (admin.items.length) items.push(admin);

      return items;
    }
  },
  watch: {
    '$i18n.locale'(val) { this.langLocal = val; }
  },
  methods: {
    endpointLabel(ep) {
      const labels = this.$brand.endpointLabels || {};
      return labels[ep] || ep;
    },
    onEndpointChange(val) {
      this.saveSubmitEndpoint(val);
      this.$emit('endpoint-changed', val);
    },
    onLangChange(val) {
      this.changeLanguage(val);
    },
    onMqChange(e) {
      this.isMobile = e.matches;
    }
  },
  mounted() {
    this._mq = window.matchMedia('(max-width: 900px)');
    this.isMobile = this._mq.matches;
    if (this._mq.addEventListener) {
      this._mq.addEventListener('change', this.onMqChange);
    } else {
      this._mq.addListener(this.onMqChange); // Safari < 14
    }
  },
  beforeDestroy() {
    if (!this._mq) return;
    if (this._mq.removeEventListener) {
      this._mq.removeEventListener('change', this.onMqChange);
    } else {
      this._mq.removeListener(this.onMqChange);
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.omx-side {
  width: $omx-shell-side-width;
  background: linear-gradient(180deg, #0b1f37 0%, #061528 100%);
  color: #cbd6e5;
  position: fixed;
  top: 0; left: 0; bottom: 0;
  display: flex;
  flex-direction: column;
  z-index: 30;
  transition: width .18s ease;

  &.is-collapsed {
    width: 64px;
  }

  &__inner {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &__brand {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 12px 10px;
    border-bottom: 1px solid rgba(255,255,255,.05);
  }
  &__brand-link {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #fff;
    flex: 1;
    min-width: 0;
    &:hover { text-decoration: none; }
  }
  &__logo-img {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    object-fit: contain;
    background: #fff;
    padding: 3px;
    flex-shrink: 0;
  }
  &__brand-text {
    display: flex;
    flex-direction: column;
    line-height: 1.1;
    min-width: 0;
    b { color: #fff; font-weight: 700; font-size: 14px; letter-spacing: -.01em; }
    small { color: #7c8aa0; font-size: 11px; font-weight: 500; }
  }
  &__collapse {
    background: rgba(255,255,255,.04);
    border: 1px solid rgba(255,255,255,.06);
    color: #cbd6e5;
    width: 26px; height: 26px;
    border-radius: 7px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    &:hover { color: #fff; background: rgba(255,255,255,.1); }
  }

  &__nav {
    flex: 1;
    overflow-y: auto;
    padding: 10px 8px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  &__group {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  &__group-label {
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: .14em;
    color: #5d6c85;
    font-weight: 700;
    padding: 8px 10px 4px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 8px;
    color: #cbd6e5;
    font-size: 13.5px;
    text-decoration: none;
    cursor: pointer;
    transition: background .12s, color .12s;
    &:hover {
      background: rgba(255,255,255,.04);
      color: #fff;
      text-decoration: none;
    }
    &.is-active {
      background: rgba(54, 146, 219, .18);
      color: #fff;
      box-shadow: inset 2px 0 0 $omx-brand-500;
      .omx-side__ico { color: #7fc1f5 !important; }
    }
  }
  &__ico {
    color: #93a3bd !important;
    flex-shrink: 0;
  }
  &__item-label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__item-count {
    background: rgba(255,255,255,.08);
    font-size: 11px;
    padding: 1px 7px;
    border-radius: 999px;
  }

  &__dev {
    padding: 4px 10px 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  &__dev-label {
    font-size: 10.5px;
    color: #7c8aa0;
    font-weight: 600;
    letter-spacing: .04em;
  }
  &__dev-row {
    margin-bottom: 4px;
    ::v-deep select {
      background: rgba(255,255,255,.06) !important;
      color: #fff !important;
      border-color: rgba(255,255,255,.1) !important;
      font-size: 12px !important;
    }
    ::v-deep .select::after {
      border-color: #cbd6e5 !important;
    }
  }


  &__user {
    border-top: 1px solid rgba(255,255,255,.06);
    padding: 10px;
  }
  &__footer {
    padding: 6px 12px 12px;
    font-size: 11px;
    color: #5d6c85;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    a {
      color: #7c8aa0;
      font-weight: 600;
      &:hover { color: #fff; text-decoration: underline; }
    }
  }
  &__footer-copy {
    text-align: center;
  }
  &__user-card {
    display: flex;
    align-items: center;
    gap: 10px;
    background: rgba(255,255,255,.04);
    border: 1px solid rgba(255,255,255,.06);
    border-radius: 10px;
    padding: 8px 10px;
    color: #cbd6e5;
    text-decoration: none;
    &:hover { background: rgba(255,255,255,.07); text-decoration: none; color: #fff; }
  }
  &__avatar {
    width: 30px; height: 30px;
    border-radius: 50%;
    background: $omx-brand-500;
    color: #fff;
    font-weight: 700;
    font-size: 11.5px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  &__user-meta {
    flex: 1;
    min-width: 0;
    line-height: 1.2;
    overflow: hidden;
    b { display: block; color: #fff; font-size: 13px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    small { color: #7c8aa0; font-size: 11px; }
  }
  &__user-more {
    background: transparent;
    border: none;
    color: #7c8aa0;
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    &:hover { color: #fff; background: rgba(255,255,255,.07); }
  }
}

// Scrim is hidden by default (desktop); only shown as a drawer backdrop on mobile.
.omx-side__scrim {
  display: none;
}

// Mobile: sidebar becomes a drawer
@media (max-width: 900px) {
  .omx-side {
    transform: translateX(-100%);
    transition: transform .22s ease;
    width: $omx-shell-side-width !important;
    box-shadow: $omx-shadow-lg;
    &.is-open-mobile {
      transform: translateX(0);
    }
  }
  // Full-viewport backdrop, sitting below the aside (z-index 30) and above content.
  // Tapping it emits close-mobile.
  .omx-side__scrim {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(11, 18, 32, .45);
    z-index: 25;
    border: none;
    cursor: pointer;
  }
}
</style>
